<template>
  <div class="pdf-output-area mt-2" :data-pdf-ready="pdfReady ? 'true' : 'false'">
    <div ref="pdfContent">
      <div v-if="billing">
        <div
          v-for="(page, pageIndex) in paginatedBilling"
          :key="pageIndex"
          class="page mt-3 mb-5"
        >
          <div 
            class="list-container mt-1" 
            :class="{ 'page-break': pageIndex !== 0 }"
          >
            <!-- ✅ ヘッダー部分（1ページ目のみ表示） -->
            <div v-if="pageIndex === 0" class="header">
              <h1 class="invoice-title text-center py-1">INVOICE</h1>

              <div class="d-flex justify-content-between">
                <div class="annual-bill-customer-rep mb-0 d-flex">
                  <!-- 顧客名(請求先) 御中 -->
                  <div class="mb-3">
                    <div class="d-flex mb-1">
                      <div>
                        <span class="text-nowrap me-2">Bill To:</span>
                      </div>
                      <div>
                        <h4 class="billing-dear-nm mb-0 align-self-start">
                          <span class="multiline-text">{{ billing.m_customer?.customer_nm }}</span>
                        </h4>
                        <!-- 顧客住所 -->
                        <div v-if="billing.billing_customer_address_display_flg === true">
                          <p v-html="(billing.customer_address || '').replace(/\n/g, '<br>')" class="mb-0 text-start customer-address"></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="one-third-block">
                  <p class="billing-dt mb-0 text-end pe-0">{{ formatDateEn(billing.billing_dt) }}</p>
                  <p class="text-end billing-no mb-0"><span>Invoice No.</span> {{ billing.parent_billing_no }}</p>
                  <div class="text-end page-no me-2 mb-2">{{ pageIndex + 1 }}/{{ paginatedBilling.length }}</div>
                </div>
              </div>

              <div class="middle-title d-flex justify-content-between" style="position: relative">
                <img
                  v-if="billing.seal_flg"
                  width="60"
                  height="60"
                  src="../../../../assets/billing_sign_img.png"
                  style="cursor: pointer; position: absolute; top: -60px; right: 140px;"
                >
                <div></div>
                <div class="text-end">
                  <div class="company-info-block">
                    <h4 class="billing-company-nm">
                      <span class="color-billing-company-orange">S</span>oei 
                      <span class="color-billing-company-orange">P</span>lanning 
                      <span class="color-billing-company-orange">C</span>o., Ltd.
                    </h4>
                    <p class="billing-company-address mb-0 text-start"><span class="me-4">2-38-6</span><span>Yoyogi, Shibuya-ku,</span></p>
                    <p class="billing-company-address mb-0 text-start"><span class="me-4">Tokyo,</span><span>151-0053</span></p>
                    <p class="billing-company-address mb-0 text-end"><span>Tel：03-3370-8006</span></p>
                    <p class="billing-company-address mb-0 text-end"><span>Fax：03-3370-8157</span></p>
                  </div>
                </div>
              </div>
            </div>

            <!-- ✅ ヘッダー部分（1ページ目のみ表示） -->
            <div v-if="pageIndex === 0" class="total-billing-amount-area mb-0">
              <div>
                <h1 class="total-billing-amount mb-0">Total Invoice Amount {{ formatCurrency(calculateGrandTotal()) }}</h1>
              </div>
              <div v-if="billing.carryover_amount > 0" class="d-flex align-items-center">
                <h5 class="carryover-amount mb-0">Carryover Amount {{ formatCurrencyZero(billing.carryover_amount) }}</h5>
              </div>
            </div>

            <!-- ✅ ページ番号（2ページ以降は単体で表示） -->
            <div v-if="pageIndex > 0" class="text-end me-2">{{ pageIndex + 1 }}/{{ paginatedBilling.length }}</div>

            <!-- 業務内容のテーブル -->
            <table class="table monthly-detail-data">
              <thead>
                <tr>
                  <th class="invoice-title text-nowrap">ID</th>
                  <th class="invoice-title billing-building-nm text-nowrap">Building</th>
                  <!-- <th class="month-data-header text-nowrap">月</th>
                  <th class="month-data-header text-nowrap">日</th> -->
                  <th class="invoice-title text-nowrap">Description</th>
                  <th v-if="showBreakdown" class="invoice-title text-nowrap">Breakdown</th>
                  <th class="invoice-title text-nowrap">Amount</th>
                </tr>
              </thead>
              <tbody>
                <!-- ▼ 各ページ内の billing 行をループ -->
                <tr v-for="(billingItem, scIndex) in page" :key="scIndex">
                  
                  <!-- 請求ID -->
                  <td>{{ billingItem.billing_no }}</td>

                  <!-- ▼ 建物列 -->
                  <template v-if="!billingItem || Object.keys(billingItem).length === 0">
                    <!-- ✅ 空行の場合（ページ補完行）：建物列を空で表示 -->
                    <td class="text-start">&nbsp;</td>
                  </template>

                  <template v-else-if="billingItem.work_type === 2">
                    <!-- ✅ 追加行はrowspanに含めず常に表示 -->
                    <td class="text-start">
                      {{ billingItem.m_building?.building_nm || '' }}
                    </td>
                  </template>

                  <template
                    v-else-if="groupBuildingsInPage(page)[scIndex]"
                  >
                    <!-- ✅ 初期データで、建物の最初の出現位置だけ rowspan 表示 -->
                    <td
                      class="text-start"
                      :rowspan="groupBuildingsInPage(page)[scIndex].count"
                    >
                      {{ billingItem.m_building?.building_nm || '' }}
                    </td>
                  </template>

                  <!-- ✅ rowspan 対象の2行目以降は建物列なし -->

                  <!-- 作業年月 -->
                  <!-- <td>{{ billingItem.work_month }}</td> -->

                  <!-- 作業日（formatDay関数で整形） -->
                  <!-- <td>{{ formatDay(billingItem.work_dt) }}</td> -->

                  <!-- 摘要 -->
                  <td class="multiline-text text-start">{{ stripMonthlyBillingLabel(billingItem.work_content) }}</td>

                  <!-- 内訳 -->
                  <td v-if="showBreakdown" class="multiline-text text-start">{{ billingItem.breakdown }}</td>

                  <!-- 金額（右寄せ通貨） -->
                  <td class="text-end">{{ formatCurrency(billingItem.bill_amount) }}</td>
                </tr>

                <!-- ▼ 最終ページなら小計・税・合計を表示 -->
                <template v-if="isLastPage(pageIndex)">
                  <!-- 内訳列がある場合は6列、それ以外は5列を colspan に指定 -->
                  <tr v-if="billing.tax_rate !== null && billing.tax_rate !== 0">
                    <td :colspan="showBreakdown ? 4 : 3" class="invoice-title">Sub Total</td>
                    <td class="text-end">{{ formatCurrency(billing.total_amount) }}</td>
                  </tr>
                  <tr v-if="billing.tax_rate !== null && billing.tax_rate !== 0">
                    <td :colspan="showBreakdown ? 4 : 3" class="invoice-title">Consumption Tax {{ billing.tax_rate }}%</td>
                    <td class="text-end">{{ formatCurrency(calculateTax()) }}</td>
                  </tr>
                  <tr>
                    <td :colspan="showBreakdown ? 4 : 3" class="invoice-title">Total</td>
                    <td class="text-end">{{ formatCurrency(calculateNoCarryoverTotal()) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>

            <!-- ✅ 振込先情報（最終ページのみ表示） -->
            <div v-if="isLastPage(pageIndex)" class="ms-4">
              <!-- 振込先１(三菱UFJ銀行) -->
              <div class="d-flex billing-bank-info">
                <div class="me-5">
                  Remittance Address
                </div>
                <div class="ms-5">
                  <p class="mb-0">Kabushikigaisha Soei Kikaku</p>
                  <p class="mb-0">[(株)創英企画]</p>
                  <p class="mb-0">Current Account {{ bankLabels.bank_01.account_no }}</p>
                  <p class="mb-0">(当座預金 {{ bankLabels.bank_01.account_no }})</p>
                  <p class="mb-0">{{ bankLabels.bank_01.name }} {{ bankLabels.bank_01.branch }}</p>
                  <p class="mb-0">(三菱UFJ銀行 新宿中央支店)</p>
                  <!-- hidden inputsはそのままでOK（日本語データ送信） -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        請求情報を読み込み中...
      </div>
    </div>
  </div>
</template>

<script>
import { savedBillingAmounts } from '@/utils/billingAmounts';
import { ref, toRefs, onMounted, computed, nextTick } from 'vue';
import { getMonthlyBilling } from '@/api/axios';
import {
  buildConsecutiveBuildingGroups,
  sortMonthlyBillingDetailsByWorkDate,
  stripMonthlyBillingLabel
} from '@/utils/monthlyBillingDetails';

export default {
  props: {
    // 値を受け取るためのpropsを定義
    billingId: {
      type: [String, Number], // 受け取る値の型
      required: true, // 必須のpropsであることを示す
    }
  },
  setup(props) {
    const userId = ref('');
    const userName = ref('');
    const customer = ref({});
    const billing = ref({});
    const pdfReady = ref(false);

    const { billingId } = toRefs(props);

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');
      pdfReady.value = false;
      
      try {
        const response = await getMonthlyBilling(billingId.value);
        billing.value = response.data.monthly_billing;
        customer.value = billing.value?.m_customer || {};
        billing.value.customer_address = format_address(customer.value) || '';
      } catch (error) {
        console.error('請求書情報の取得に失敗しました:', error);
      } finally {
        await nextTick();
        pdfReady.value = true;
      }
    };

    const bankLabels = computed(() => ({
      bank_01: {
        bank_display_flg: true,
        name: 'MUFG Bank, Ltd.',
        branch: 'Shinjuku Central Branch',
        type: 'Current Account',
        account_no: billing.value.account_no_01
      }
    }));

    const splitIntoPages = (items, itemsPerPage) => {
      const result = [];
      for (let i = 0; i < items.length; i += itemsPerPage) {
        const page = items.slice(i, i + itemsPerPage);
        // 足りない分を空オブジェクトで埋める
        while (page.length < itemsPerPage) {
          page.push({}); // 空オブジェクトを挿入
        }
        result.push(page);
      }
      return result;
    };

    const paginatedBilling = computed(() => {
      const details = sortMonthlyBillingDetailsByWorkDate(billing.value.t_customer_monthly_billing_details || []);
      return splitIntoPages(details, 14);
    });

    const isLastPage = (pageIndex) => {
      return pageIndex === paginatedBilling.value.length - 1;
    };

    // 顧客情報から住所をフォーマットする関数（英語）
    const format_address = (customer) => {
      if (!customer) return '';
      const line1 = [customer.other_address, customer.city].filter(Boolean).join(', ');
      const line2 = [customer.prefecture, customer.postal_code].filter(Boolean).join(', ');
      return `${line1}\n${line2}`;
    };

    /**
     * ページ内の billingItem を建物ごとにグループ化。
     * ただし、追加項目（work_type === 2）は含めない。
     * 
     * @param {Array} page - 表示対象の1ページ分の請求明細データ
     * @returns {Object} 建物IDごとの出現位置と行数
     */
    const groupBuildingsInPage = (page) => {
      return buildConsecutiveBuildingGroups(page);
    };

    // breakdown（内訳）を表示すべきかを判断する computed 変数
    // billing.value の中に breakdown が null/空でないデータが1件でもあるかをチェック
    const showBreakdown = computed(() => {
      return (billing.value?.t_customer_monthly_billing_details || []).some(
        item => item.breakdown != null && item.breakdown !== ''
      );
    });
    
    // 日時フォーマット変換メソッド
    const formatDateTime = (dateTime) => {
      if (!dateTime) {
        return '';
      }
      const date = new Date(dateTime);
      return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    };

    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;  // 日付部分だけを取得
    };
    
    const formatDateEn = (date) => {
      if (!date) return '';
      const d = new Date(date);
      const day = d.getDate();
      const monthShort = d.toLocaleString('en-US', { month: 'short' });
      const yearShort = d.getFullYear().toString().slice(-2);
      return `${day}-${monthShort}-${yearShort}`;
    };

    // const formatDay = (dateString) => {
    //   if (!dateString) return '';
    //   const date = new Date(dateString);
    //   return date.getDate();  // 日付部分だけを取得
    // };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '';
    };

    // Format currency for zero
    const formatCurrencyZero = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    const calculateTax = () => savedBillingAmounts(billing.value).tax_amount;

    // 合計金額の計算（繰越金額を除く）
    const calculateNoCarryoverTotal = () => savedBillingAmounts(billing.value).current_billing_amount;

    // 合計金額の計算（繰越金額を含む）
    const calculateGrandTotal = () => savedBillingAmounts(billing.value).billing_total_amount;

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      billing,
      pdfReady,
      paginatedBilling,
      isLastPage,
      groupBuildingsInPage,
      showBreakdown,
      formatDateTime,
      formatYearMonthDay,
      // formatDay,
      formatCurrency,
      formatCurrencyZero,
      calculateTax,
      calculateNoCarryoverTotal,
      calculateGrandTotal,
      formatDateEn,
      stripMonthlyBillingLabel,
      bankLabels
    };
  }
};
</script>

<style scoped>
.list-container {
  width: 100%;
  margin: 0 auto;
}

.invoice-title{
  background-color: #77b8de !important;
  color: #fff;
}

.header h4.billing-dear-nm {
  font-size: 14px;
  padding: 4px 8px 4px 0;
}

.header .page-no{
  font-size: 11px;
}

.header .billing-no{
  font-size: 11px;
}

.header p.billing-dt{
  font-size: 11px;
  font-weight: bold;
  padding-right: 8px;
}

.customer-address{
  font-size: 11px;
}

.color-billing-company-orange {
  color: #ff6600;
}

.header .billing-company-nm { font-size: 18px; }
.header .billing-company-representative { font-size: 12px; }
.header .billing-company-address { font-size: 12px; }
.header .company-regist-no { font-size: 12px; }

.total-billing-amount-area .total-billing-amount{
  display: inline-block;
  font-size: 20px;
}
.total-billing-amount-area .carryover-amount { font-size: 12px; }

.monthly-detail-data {
  margin: 4px auto;
  width: 100%;
  border-collapse: collapse;
}

.monthly-detail-data th, 
.monthly-detail-data td {
  border-bottom: 1px solid #000;
  border-right: 1px solid #000;
  padding: 4px 8px;
  text-align: center;
  font-size: 12px;
  height: 32px;
}

.monthly-detail-data th {
  border-top: 1px solid #000;
}

.monthly-detail-data td {
  background-color: #fff;
}

.monthly-detail-data th:first-child,
.monthly-detail-data td:first-child{
  border-left: 1px solid #000;
}

.monthly-detail-data th.billing-building-nm {
  min-width: 200px !important;
}

.monthly-detail-data th.month-day-width{
  width: 36px;
}

.monthly-detail-data th.month-data-header {
  background-color: #f0f8ff;
  font-weight: bold;
}

.billing-bank-info{
  font-size: 12px;
}

.page-break {
  page-break-before: always;
}
</style>
